/* eslint-disable */
/* eslint-disable no-restricted-globals, no-undef */
import React, { useEffect, useState } from "react";
import { 
  Box, 
  Flex, 
  Heading, 
  Text, 
  Spinner, 
  SimpleGrid, 
  Badge, 
  Avatar, 
  VStack, 
  HStack,
  Button,
  Input,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  useColorModeValue
} from "@chakra-ui/react";

import axios from "axios";
import { FaChalkboardTeacher, FaVideo, FaGraduationCap } from "react-icons/fa";
import { FiUsers, FiClock, FiCheckCircle } from "react-icons/fi";
import { useModalToast } from "../../context/GlobalModalToast";

const TeacherInsights = () => {
  const [insights, setInsights] = useState([]);
  const { toast, confirm } = useModalToast();
  const [loading, setLoading] = useState(true);
  const [complianceSearch, setComplianceSearch] = useState("");

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const token = JSON.parse(localStorage.getItem('user'))?.token || "";
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/reports/teacher-insights`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInsights(res.data);
      } catch (err) {
        console.error("Failed to load teacher insights", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInsights();
  }, []);

  const bgMain = useColorModeValue("var(--background)", "gray.900");
  const textMain = useColorModeValue("gray.800", "gray.100");
  const textSub = useColorModeValue("gray.500", "gray.400");
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.100", "gray.700");
  const accordionBg1 = useColorModeValue("gray.50", "gray.700");
  const accordionHover1 = useColorModeValue("gray.100", "gray.600");
  const accordionBg2 = useColorModeValue("blue.50", "blue.900");
  const accordionHover2 = useColorModeValue("blue.100", "blue.800");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const itemBg1 = useColorModeValue("purple.50", "purple.900");
  const itemText1 = useColorModeValue("purple.900", "purple.100");
  const itemBorder = useColorModeValue("blue.100", "blue.800");
  const itemBg2 = useColorModeValue("white", "gray.700");
  const inputBg = useColorModeValue("white", "gray.700");

  return (
    <Box mx="auto" w="full" bg={bgMain} minH="100vh" pb={10} color={textMain}>
      <Box pt="90px" px={{ base: 4, md: 8, lg: 10 }}>
        
        
        <Flex justify="space-between" align="flex-end" mb={8} mt={4} wrap="wrap" gap={4}>
          <Box>
            <Badge colorScheme="purple" variant="subtle" px={3} py={1} borderRadius="full" fontSize="10px" fontWeight="800" mb={3} letterSpacing="0.5px">
              🌟 Admin Operations
            </Badge>
            <Heading size="lg" fontWeight="900" letterSpacing="-0.5px" color={textMain}>
              Instructor Insights & Compliance
            </Heading>
            <Text fontSize="sm" color={textSub} mt={1} fontWeight="500">
              Drill down into instructor performance, course counts, live sessions, and their employees' compliance.
            </Text>
          </Box>
        </Flex>

        {loading ? (
          <Flex justify="center" p={20}><Spinner size="xl" color="purple.500" thickness="4px" /></Flex>
        ) : (
          <SimpleGrid columns={1} spacing={6}>
            {insights.map((teacher, idx) => (
              <Box 
                key={teacher.teacherId} 
                bg={cardBg} 
                p={6} 
                borderRadius="3xl" 
                boxShadow="var(--shadow-premium)" 
                border="1px solid" 
                borderColor={cardBorder}
              >
                {/* Teacher Header Row */}
                <Flex justify="space-between" align="center" flexWrap="wrap" gap={4} mb={6}>
                  <HStack spacing={4}>
                    <Avatar size="lg" name={teacher.teacherName} bg="purple.500" color="white" />
                    <Box>
                      <Heading size="md" fontWeight="800" color={textMain}>{teacher.teacherName}</Heading>
                      <Text fontSize="xs" fontWeight="600" color={textSub}>{teacher.teacherEmail} • {teacher.department || "Global"}</Text>
                    </Box>
                  </HStack>
                  <HStack spacing={6}>
                    <VStack align="flex-end" spacing={0}>
                      <Text fontSize="10px" color={textSub} fontWeight="700">TOTAL COURSES</Text>
                      <HStack><Icon as={FaGraduationCap} color="purple.500" /><Text fontWeight="900" fontSize="lg">{teacher.totalCourses}</Text></HStack>
                    </VStack>
                    <VStack align="flex-end" spacing={0}>
                      <Text fontSize="10px" color={textSub} fontWeight="700">EMPLOYEES</Text>
                      <HStack><Icon as={FiUsers} color="blue.500" /><Text fontWeight="900" fontSize="lg">{teacher.studentsAssigned}</Text></HStack>
                    </VStack>
                    <VStack align="flex-end" spacing={0}>
                      <Text fontSize="10px" color={textSub} fontWeight="700">COMPLIANCE RATE</Text>
                      <HStack><Icon as={FiCheckCircle} color="green.500" /><Text fontWeight="900" fontSize="lg">{teacher.compliance.completionRate}%</Text></HStack>
                    </VStack>
                  </HStack>
                </Flex>

                {/* Drilldown Accordion */}
                <Accordion allowMultiple>
                  {/* Courses & Compliance Breakdown */}
                  <AccordionItem border="none" mb={4}>
                    <h2>
                      <AccordionButton bg={accordionBg1} borderRadius="xl" _hover={{ bg: accordionHover1 }} p={4}>
                        <Box flex="1" textAlign="left" fontWeight="800" fontSize="sm">
                          📚 Course Portfolio & Student Compliance Breakdown
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4} pt={4}>
                      <Flex gap={8}>
                        {/* Courses List */}
                        <Box flex={1}>
                          <Text fontWeight="800" fontSize="xs" color={textSub} mb={3} textTransform="uppercase">Active Courses ({teacher.courses.length})</Text>
                          <VStack align="stretch" spacing={2}>
                            {teacher.courses.length > 0 ? teacher.courses.map(c => (
                              <Flex key={c._id} p={3} bg={itemBg1} borderRadius="lg" justify="space-between" align="center">
                                <Text fontWeight="700" fontSize="sm" color={itemText1}>{c.title}</Text>
                                <Badge colorScheme={c.isInviteOnly ? "red" : "green"} fontSize="10px">{c.isInviteOnly ? "Private" : "Public"}</Badge>
                              </Flex>
                            )) : <Text fontSize="xs" color={textSub}>No courses published yet.</Text>}
                          </VStack>
                        </Box>

                        {/* Student Compliance Data Table */}
                        <Box flex={2}>
                           <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
                             <Text fontWeight="800" fontSize="xs" color={textSub} textTransform="uppercase">Enrolled Employee Compliance ({teacher.studentsAssigned} employees · {teacher.enrollmentDetails.length} records)</Text>
                             <Input 
                               placeholder="Search name or course..."
                               value={complianceSearch}
                               onChange={(e) => setComplianceSearch(e.target.value)}
                               size="xs"
                               borderRadius="lg"
                               w="180px"
                               bg={inputBg}
                             />
                           </Flex>
                           <Box overflowX="auto" border="1px solid" borderColor={cardBorder} borderRadius="xl">
                             <Table size="sm" variant="simple">
                               <Thead bg={tableHeaderBg}>
                                 <Tr>
                                   <Th fontSize="10px">Student</Th>
                                   <Th fontSize="10px">Course</Th>
                                   <Th fontSize="10px">Status</Th>
                                   <Th fontSize="10px">Due Date</Th>
                                 </Tr>
                               </Thead>
                               <Tbody>
                                 {(() => {
                                   const filtered = (teacher.enrollmentDetails || []).filter(e => {
                                     const studentName = e.userId?.fullName || '';
                                     const courseTitle = e.courseId?.title || '';
                                     const q = complianceSearch.toLowerCase();
                                     return studentName.toLowerCase().includes(q) || courseTitle.toLowerCase().includes(q);
                                   });

                                   if (filtered.length === 0) {
                                     return <Tr><Td colSpan={4} textAlign="center" fontSize="xs" color={textSub} py={4}>No student enrollments found.</Td></Tr>;
                                   }

                                   return filtered.map(e => (
                                     <Tr key={e._id}>
                                       <Td fontWeight="700" fontSize="xs">{e.userId?.fullName || "Unknown"}</Td>
                                       <Td fontSize="xs" color={textSub}>{e.courseId?.title}</Td>
                                       <Td>
                                         <Badge 
                                            colorScheme={
                                              e.status === 'Completed' ? 'green' : 
                                              e.status === 'In Progress' ? 'orange' : 'gray'
                                            }
                                         >
                                           {e.status}
                                         </Badge>
                                       </Td>
                                       <Td fontSize="xs" color={e.dueDate && new Date() > new Date(e.dueDate) && e.status !== 'Completed' ? "red.500" : textSub} fontWeight={e.dueDate && new Date() > new Date(e.dueDate) && e.status !== 'Completed' ? "bold" : "normal"}>
                                         {e.dueDate ? new Date(e.dueDate).toLocaleDateString() : "None"}
                                       </Td>
                                     </Tr>
                                   ));
                                 })()}
                               </Tbody>
                             </Table>
                           </Box>
                        </Box>
                      </Flex>
                    </AccordionPanel>
                  </AccordionItem>

                  {/* Live Sessions */}
                  <AccordionItem border="none">
                    <h2>
                      <AccordionButton bg={accordionBg2} borderRadius="xl" _hover={{ bg: accordionHover2 }} p={4}>
                        <Box flex="1" textAlign="left" fontWeight="800" fontSize="sm" color={itemText1}>
                          🎙️ Live Training Sessions ({teacher.activeLiveSessions})
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4} pt={4}>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        {teacher.liveSessions.length > 0 ? teacher.liveSessions.map(ls => (
                          <Box key={ls._id} p={4} border="1px solid" borderColor={itemBorder} borderRadius="xl" bg={itemBg2}>
                            <Flex justify="space-between" mb={2}>
                              <Badge colorScheme="blue">{ls.targetSector}</Badge>
                              <HStack color={textSub} fontSize="xs"><Icon as={FiClock} /><Text>{ls.duration}m</Text></HStack>
                            </Flex>
                            <Heading size="sm" mb={1}>{ls.title}</Heading>
                            <Text fontSize="xs" color={textSub} mb={3}>{new Date(ls.scheduledAt).toLocaleString()}</Text>
                            <HStack spacing={2} mt={3}>
                              <Button size="xs" colorScheme="blue" variant="outline" as="a" href={ls.meetingLink.startsWith('http') ? ls.meetingLink : `https://${ls.meetingLink}`} target="_blank">
                                Join Meeting
                              </Button>
                              <Button 
                                size="xs" 
                                colorScheme="red" 
                                variant="ghost"
                                borderRadius="full"
                                onClick={async () => {
                                  { const _c = await confirm({ title: "Confirm", description: "Are you sure you want to cancel this live session?", status: "warning" }); if (!_c) return; }
                                  try {
                                    const token = JSON.parse(localStorage.getItem('user'))?.token || "";
                                    await axios.delete(`${process.env.REACT_APP_API_URL}/api/livesessions/${ls._id}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    // Refresh insights list
                                    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/reports/teacher-insights`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    setInsights(res.data);
                                  } catch (err) {
                                    toast({ title: err.response?.data?.message || "Failed to cancel live session", status: "info" });
                                  }
                                }}
                              >
                                 Cancel Session
                               </Button>
                             </HStack>
                           </Box>
                         )) : <Text fontSize="xs" color={textSub}>No active live sessions scheduled.</Text>}
                       </SimpleGrid>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
                
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>
    </Box>
  );
};

export default TeacherInsights;
